import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin,
  Users,
  Activity,
  Building2,
  Calendar,
  ChevronLeft,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatDate } from '../lib/dates';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getBeneficiaries = (activity = {}) =>
  Number(
    activity.total_beneficiaries ??
      activity.beneficiarios_totales ??
      activity.total_beneficiarios ??
      0
  ) || 0;

const getDependencyName = (activity = {}, depsMap = {}) => {
  const depId = activity.dependency_id;
  return (
    activity.dependency_name ||
    depsMap?.[depId]?.name ||
    'Dependencia no disponible'
  );
};

const getDependencyAcronym = (activity = {}, depsMap = {}) => {
  const depId = activity.dependency_id;
  return (
    depsMap?.[depId]?.acronym ||
    activity.dependency_name?.substring(0, 8) ||
    'Otro'
  );
};

const getActivityDateLabel = (activity = {}) =>
  formatDate(
    activity.week_start || activity.start_date || activity.created_at?.slice(0, 10)
  ) || '—';

export const TerritoryDetail = () => {
  const { id } = useParams();

  const [territory, setTerritory] = useState(null);
  const [activities, setActivities] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [territoryRes, activitiesRes, statsRes, depsRes] = await Promise.all([
          axios.get(`${API}/territories/${id}`),

            axios.get(`${API}/activities?territory_id=${id}&status=published`),

          axios.get(`${API}/stats/by-territory`),
          axios.get(`${API}/dependencies`),
        ]);

        setTerritory(territoryRes.data || null);
        setActivities(Array.isArray(activitiesRes.data) ? activitiesRes.data : []);
        setStats(statsRes.data?.[id] || {});
        setDependencies(Array.isArray(depsRes.data) ? depsRes.data : []);
      } catch (error) {
        console.error('Error cargando detalle de territorio:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const depsMap = useMemo(() => {
    return dependencies.reduce((acc, dep) => {
      acc[dep.id] = dep;
      return acc;
    }, {});
  }, [dependencies]);

  const chartData = useMemo(() => {
    const grouped = activities.reduce((acc, act) => {
      const depId = act.dependency_id || 'sin-dependencia';
      const acronym = getDependencyAcronym(act, depsMap);
      const fullName = getDependencyName(act, depsMap);
      const beneficiarios = getBeneficiaries(act);

      if (!acc[depId]) {
        acc[depId] = {
          name: acronym,
          fullName,
          beneficiarios: 0,
          actividades: 0,
        };
      }

      acc[depId].beneficiarios += beneficiarios;
      acc[depId].actividades += 1;

      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => b.beneficiarios - a.beneficiarios);
  }, [activities, depsMap]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E1]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#8B1C4A]/20" />
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!territory) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E1]">
        <div className="text-center">
          <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Territorio no encontrado</p>
          <Link to="/territorios">
            <Button className="mt-4">Volver a Territorios</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E1]" data-testid="territory-detail">
      <section className="bg-[#8B1C4A] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/territorios"
            className="inline-flex items-center gap-1 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver a Territorios
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div>
              <Badge className="bg-white/20 text-white mb-2">{territory.code}</Badge>
              <h1 className="text-3xl lg:text-4xl font-bold tracking-tight">
                {territory.name}
              </h1>
              <p className="text-white/80 mt-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {territory.alcaldia}
              </p>
            </div>

            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold">
                  {(stats?.total_beneficiarios || stats?.total_beneficiados || 0).toLocaleString()}
                </p>
                <p className="text-white/80 text-sm">Beneficiarios</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">{stats?.total_activities || 0}</p>
                <p className="text-white/80 text-sm">Actividades</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">


          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {chartData.length > 0 && (
                <Card className="border border-[#D4C5A5]/30 mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-[#8B1C4A]" />
                      Impacto por Dependencia
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#D4C5A5" />
                          <XAxis type="number" />
                          <YAxis
                            dataKey="name"
                            type="category"
                            width={100}
                            tick={{ fontSize: 11 }}
                          />
                          <Tooltip
                            formatter={(value, name) => [value, name]}
                            labelFormatter={(label, payload) =>
                              payload?.[0]?.payload?.fullName || label
                            }
                          />
                          <Bar
                            dataKey="beneficiarios"
                            fill="#8B1C4A"
                            name="Beneficiarios"
                            radius={[0, 4, 4, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="border border-[#D4C5A5]/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#BC955C]" />
                    Actividades Registradas ({activities.length})
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  {activities.length > 0 ? (
                    <div className="space-y-4">
                      {activities.map((activity) => {
                        const beneficiaries = getBeneficiaries(activity);
                        const dependencyName = getDependencyName(activity, depsMap);
                        const activityDate = getActivityDateLabel(activity);

                        return (
                          <Link
                            key={activity.id}
                            to={`/actividad/${activity.id}`}
                            className="block"
                          >
                            <div className="p-4 rounded-lg border border-slate-200 hover:border-[#8B1C4A]/50 hover:shadow-sm transition-all">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <h3 className="font-semibold text-slate-800">
                                    {activity.title || 'Actividad sin título'}
                                  </h3>

                                  <p className="text-sm text-slate-500 mt-1">
                                    {dependencyName}
                                  </p>

                                  <div className="flex items-center gap-4 mt-2 text-sm">
                                    <span className="flex items-center gap-1 text-[#8B1C4A]">
                                      <Users className="w-4 h-4" />
                                      {beneficiaries.toLocaleString()}
                                    </span>

                                    <span className="flex items-center gap-1 text-slate-500">
                                      <Calendar className="w-4 h-4" />
                                      {activityDate}
                                    </span>
                                  </div>
                                </div>

                                {activity.photos?.length > 0 && (
                                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                                    <img
                                      src={
                                        activity.photos[0].startsWith('/api')
                                          ? `${process.env.REACT_APP_BACKEND_URL}${activity.photos[0]}`
                                          : activity.photos[0]
                                      }
                                      alt=""
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">
                        No hay actividades registradas en este territorio
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border border-[#D4C5A5]/30">
                <CardHeader>
                  <CardTitle className="text-lg">Información</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Código</p>
                    <p className="font-medium">{territory.code}</p>
                  </div>

                  <div>
                    <p className="text-sm text-slate-500">Alcaldía</p>
                    <p className="font-medium">{territory.alcaldia}</p>
                  </div>

                  <div>
                    <p className="text-sm text-slate-500">Coordenadas</p>
                    <p className="font-mono text-sm">
                      {Number(territory.lat).toFixed(4)}, {Number(territory.lng).toFixed(4)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-[#D4C5A5]/30 bg-[#8B1C4A] text-white">
                <CardContent className="p-6 text-center">
                  <p className="text-white/80 text-sm mb-2">Total Acciones</p>
                  <p className="text-4xl font-bold">{stats?.total_acciones || 0}</p>
                  <p className="text-white/60 text-sm mt-2">en este territorio</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TerritoryDetail;